"use client";

import { AccessControlProvider } from "@refinedev/core";

// This will be populated by the access control hook
let companyContextRef: {
  hasPermission: (resource: string, action: string) => boolean;
  canAccessResource: (resourceID: string) => boolean;
  currentCompanyAccessRights: any;
} | null = null;

export const setAccessControlContext = (context: {
  hasPermission: (resource: string, action: string) => boolean;
  canAccessResource: (resourceID: string) => boolean;
  currentCompanyAccessRights: any;
}) => {
  companyContextRef = context;
};

// Services a resource-restricted user may open, or null when unrestricted.
// Populated by AccessibleServicesSync once the links have resolved.
let accessibleServiceIDsRef: Set<string> | null = null;

export const setAccessibleServiceIDs = (serviceIDs: Set<string> | null) => {
  accessibleServiceIDsRef = serviceIDs;
};

export const accessControlProvider: AccessControlProvider = {
  can: async ({ resource, action, params }) => {
    // If no context yet, allow everything (during initialization)
    if (!companyContextRef) {
      return { can: true };
    }

    const { hasPermission, canAccessResource } = companyContextRef;

    // Map refine actions to our permission actions
    let permissionAction: string;
    switch (action) {
      case "list":
      case "show":
        permissionAction = "read";
        break;
      case "create":
      case "edit":
        permissionAction = "edit";
        break;
      case "delete":
        permissionAction = "delete";
        break;
      default:
        permissionAction = "read";
    }

    // Map resource names to permission categories
    let permissionResource: string;
    if (resource === "services") {
      permissionResource = "services";
    } else if (resource === "resources") {
      permissionResource = "resources";
    } else if (resource === "clients") {
      permissionResource = "clients";
    } else if (resource === "bookings") {
      permissionResource = "bookings";
    } else if (resource === "companies") {
      permissionResource = "companies";
    } else if (
      resource?.includes("plugin") ||
      resource === "subscription" ||
      resource === "giftcards" ||
      resource === "emails" ||
      resource === "addons"
    ) {
      permissionResource = "plugins";
    } else {
      // For other resources, allow by default
      return { can: true };
    }

    // Check permission for the resource and action
    const hasPerm = hasPermission(permissionResource, permissionAction);

    if (!hasPerm) {
      return {
        can: false,
        reason: "You do not have permission to perform this action",
      };
    }

    // Additional check for resources: verify access to specific resource ID
    if (resource === "resources" && params?.id) {
      const canAccess = canAccessResource(`${params?.id}`);
      if (!canAccess) {
        return {
          can: false,
          reason: "You do not have access to this resource",
        };
      }
    }

    // Additional check for services: a restricted user may only open services
    // linked to a resource they can access. A null set means unrestricted.
    if (resource === "services" && params?.id && accessibleServiceIDsRef) {
      if (!accessibleServiceIDsRef.has(`${params?.id}`)) {
        return {
          can: false,
          reason: "You do not have access to this service",
        };
      }
    }

    return { can: true };
  },
};
